import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  RichTextEditor,
  SectionCard,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  RequiredMark,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import {
  COLLECTION_MALLS,
  COLLECTION_MALL_LABELS,
  DEFAULT_COLLECTION_MALL,
  type CollectionMall,
} from '@/features/catalog/productCollection/collectionMalls';
import {
  useEventConsentTermMutation,
  useEventConsentTermsQuery,
  type EventConsentTermPayload,
  type EventConsentTermView,
} from '../hooks/useEventConsentTermsQuery';

interface Props {
  eventId: string;
}

interface MallProps {
  eventId: string;
  mall: CollectionMall;
}

/** 기존 약관 → payload(부분 변경 시 나머지 보존). */
const toPayload = (
  term: EventConsentTermView,
  patch: Partial<EventConsentTermPayload>,
): EventConsentTermPayload => ({
  title: term.title,
  contentHtml: term.contentHtml ?? null,
  linkUrl: term.linkUrl ?? null,
  required: term.required,
  sortOrder: term.sortOrder,
  ...patch,
});

interface TermCardProps {
  term: EventConsentTermView;
  index: number;
  total: number;
  disabled: boolean;
  onPatch: (patch: Partial<EventConsentTermPayload>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

/**
 * 개별 동의 약관 카드 — 제목/필수/약관 본문(contentHtml)/외부 링크 편집 + 정렬/삭제.
 * 텍스트는 로컬 버퍼 + blur 시 1회 PUT(키 입력마다 PUT 방지), 필수 토글/정렬/삭제는 즉시.
 * 본문(RichTextEditor)은 onChange 가 빈번하므로 blur(에디터 컨테이너) 대신 값 변경 후
 * "본문 저장" 동선 없이도 안전하게 onChange→로컬→blur 커밋이 어렵다 → 본문은 변경 즉시
 * 로컬에 담고 별도 "본문 저장" 버튼으로 commit(긴 약관 편집 중 매 키 PUT 방지).
 */
const TermCard = ({ term, index, total, disabled, onPatch, onMove, onRemove }: TermCardProps) => {
  const [title, setTitle] = useState(term.title);
  const [linkUrl, setLinkUrl] = useState(term.linkUrl ?? '');
  const [contentHtml, setContentHtml] = useState(term.contentHtml ?? '');
  const [contentDirty, setContentDirty] = useState(false);

  // 서버 값이 외부에서 바뀌면 로컬 버퍼 동기화(다른 카드의 PUT 으로 목록 재조회 등).
  const syncToken = `${term.title}|${term.linkUrl ?? ''}|${term.contentHtml ?? ''}`;
  const [syncKey, setSyncKey] = useState(syncToken);
  if (syncKey !== syncToken) {
    setTitle(term.title);
    setLinkUrl(term.linkUrl ?? '');
    setContentHtml(term.contentHtml ?? '');
    setContentDirty(false);
    setSyncKey(syncToken);
  }

  const commitTitle = () => {
    const next = title.trim();
    if (next === '' || next === term.title) {
      setTitle(term.title); // 빈 제목은 거부(BE @NotBlank) → 직전 값 복원.
      return;
    }
    onPatch({ title: next });
  };

  const commitLink = () => {
    const next = linkUrl.trim();
    if (next === (term.linkUrl ?? '')) return;
    onPatch({ linkUrl: next === '' ? null : next });
  };

  const commitContent = () => {
    onPatch({ contentHtml: contentHtml.trim() === '' ? null : contentHtml });
    setContentDirty(false);
  };

  return (
    <li
      className="flex flex-col gap-3 rounded-md p-4"
      style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
      data-testid="event-consent-term-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-1 flex-col gap-1">
          <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
            동의 항목 제목
            <RequiredMark />
          </span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            maxLength={200}
            disabled={disabled}
            placeholder="예: 개인정보 수집·이용 동의"
            aria-label="동의 항목 제목"
          />
        </div>
        <label
          className="flex items-center gap-2 pt-6 text-[13px]"
          style={{ color: 'var(--content-default)' }}
        >
          <Switch
            checked={term.required}
            onCheckedChange={(v) => onPatch({ required: v })}
            disabled={disabled}
            aria-label={`${term.title} 필수 동의`}
          />
          필수 동의
        </label>
        <div className="flex items-center gap-1 pt-5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!px-1.5"
            onClick={() => onMove(-1)}
            disabled={disabled || index <= 0}
            aria-label={`${term.title} 위로`}
          >
            <ChevronUp size={14} aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!px-1.5"
            onClick={() => onMove(1)}
            disabled={disabled || index >= total - 1}
            aria-label={`${term.title} 아래로`}
          >
            <ChevronDown size={14} aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="!px-1.5"
            onClick={onRemove}
            disabled={disabled}
            aria-label={`${term.title} 삭제`}
          >
            <Trash2 size={14} style={{ color: 'var(--signal-danger)' }} aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* 약관 본문(contentHtml) — 긴 약관은 매 키 PUT 방지 위해 "본문 저장"으로 커밋. SF PDP 는 SafeHtml sanitize 렌더. */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
            약관 본문 (선택 — 링크만 사용 시 비움)
          </span>
          {contentDirty ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="!h-7 !px-2 text-[12px]"
              onClick={commitContent}
              disabled={disabled}
              data-testid="event-consent-term-content-save"
            >
              본문 저장
            </Button>
          ) : null}
        </div>
        <RichTextEditor
          value={contentHtml}
          onChange={(html) => {
            setContentHtml(html);
            setContentDirty(true);
          }}
          minHeight={140}
          aria-label={`${term.title} 약관 본문`}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
          외부 약관 링크 (선택)
        </span>
        <Input
          type="url"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onBlur={commitLink}
          maxLength={500}
          disabled={disabled}
          placeholder="https://…"
          aria-label="외부 약관 링크"
        />
      </div>
    </li>
  );
};

/** 이벤트 동의 약관(몰별) — 고정 3종 시드 위에 제목/본문/링크/필수 편집 + 추가/삭제/정렬. */
const MallEventConsentTerms = ({ eventId, mall }: MallProps) => {
  const query = useEventConsentTermsQuery(eventId, mall);
  const { create, update, remove } = useEventConsentTermMutation(eventId, mall);

  const [newTitle, setNewTitle] = useState('');
  const [newRequired, setNewRequired] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const validEventId = !!eventId && eventId !== 'new';

  const terms = useMemo(
    () => (query.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [query.data],
  );

  const busy = create.isPending || update.isPending || remove.isPending;

  const handleAdd = () => {
    if (!validEventId) {
      setError('먼저 이벤트를 저장한 뒤 동의 항목을 추가할 수 있습니다.');
      return;
    }
    if (newTitle.trim() === '') {
      setError('동의 항목 제목을 입력하세요.');
      return;
    }
    setError(null);
    const nextSort = terms.length > 0 ? Math.max(...terms.map((t) => t.sortOrder)) + 1 : 0;
    create.mutate(
      { title: newTitle.trim(), contentHtml: null, linkUrl: null, required: newRequired, sortOrder: nextSort },
      {
        onSuccess: () => {
          toast.success('동의 항목이 추가되었습니다. 약관 본문은 아래에서 작성하세요.');
          setNewTitle('');
          setNewRequired(true);
        },
        onError: (err) => setError(friendlyMessage(err, '추가에 실패했습니다.')),
      },
    );
  };

  const patchTerm = (term: EventConsentTermView, patch: Partial<EventConsentTermPayload>) => {
    update.mutate(
      { termId: term.termId, body: toPayload(term, patch) },
      { onError: (err) => toast.error(friendlyMessage(err, '변경에 실패했습니다.')) },
    );
  };

  // 인접 약관과 sortOrder 교환(↑↓). 두 건 PUT.
  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= terms.length) return;
    const a = terms[index];
    const b = terms[next];
    if (!a || !b) return;
    patchTerm(a, { sortOrder: b.sortOrder });
    patchTerm(b, { sortOrder: a.sortOrder });
  };

  const handleRemove = (term: EventConsentTermView) => {
    remove.mutate(term.termId, {
      onSuccess: () => toast.success('동의 항목이 삭제되었습니다.'),
      onError: (err) => toast.error(friendlyMessage(err, '삭제에 실패했습니다.')),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        title="동의 약관 추가"
        description="응모 시 노출되는 개별 동의 항목입니다. 추가 후 약관 본문(상세)을 작성하세요. 신규 이벤트는 고정 3종(개인정보/배송일정/미성년자)이 기본 제공되며 수정·삭제할 수 있습니다."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
              동의 항목 제목
              <RequiredMark />
            </span>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="예: 개인정보 수집·이용 동의"
              maxLength={200}
              disabled={busy || !validEventId}
              className="max-w-[280px]"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
          </div>
          <label
            className="flex items-center gap-2 pb-2 text-[12px]"
            style={{ color: 'var(--content-default)' }}
          >
            <Switch
              checked={newRequired}
              onCheckedChange={setNewRequired}
              disabled={busy || !validEventId}
              aria-label="새 동의 항목 필수"
            />
            필수 동의
          </label>
          <Button
            size="sm"
            className="ml-auto"
            onClick={handleAdd}
            disabled={busy || !validEventId}
            data-testid="event-consent-term-add"
          >
            <Plus size={15} aria-hidden="true" />
            추가
          </Button>
        </div>
        {!validEventId ? (
          <p className="mt-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
            먼저 이벤트를 저장한 뒤 동의 항목을 설정할 수 있습니다.
          </p>
        ) : null}
        {error ? (
          <p className="mt-2 text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert">
            {error}
          </p>
        ) : null}
      </SectionCard>

      <SectionCard title="동의 약관" description="제목/본문/링크/필수 여부와 순서(↑↓)를 편집합니다.">
        {validEventId && query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[120px] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-8 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>동의 항목을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : terms.length === 0 ? (
          <div
            className="flex items-center justify-center rounded-md py-8 text-[13px]"
            style={{
              background: 'var(--surface-0)',
              border: '1px dashed var(--divider-strong)',
              color: 'var(--content-muted)',
            }}
          >
            동의 항목이 없습니다. 위에서 항목을 추가하세요.
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {terms.map((term, index) => (
              <TermCard
                key={term.termId}
                term={term}
                index={index}
                total={terms.length}
                disabled={busy}
                onPatch={(patch) => patchTerm(term, patch)}
                onMove={(dir) => move(index, dir)}
                onRemove={() => handleRemove(term)}
              />
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
};

/**
 * R4-A ⑤ — 이벤트 응모 동의 약관(상세 본문 포함) 편집기. 4몰 탭(국내/일문/중문/영문).
 * be-catalog `event_consent_terms`(상품 ProductConsentTerm 1:1 미러) per-event + per-mall.
 * SF 응모폼은 ConsentTermRow + SafeHtml(contentHtml) 로 렌더(fe-sf-o).
 */
export const EventConsentTermsForm = ({ eventId }: Props) => {
  const [activeMall, setActiveMall] = useState<CollectionMall>(DEFAULT_COLLECTION_MALL);

  return (
    <Tabs value={activeMall} onValueChange={(v) => setActiveMall(v as CollectionMall)} className="w-full">
      <TabsList className="mb-4">
        {COLLECTION_MALLS.map((mall) => (
          <TabsTrigger key={mall} value={mall} data-testid={`event-consent-mall-tab-${mall}`}>
            {COLLECTION_MALL_LABELS[mall]}
          </TabsTrigger>
        ))}
      </TabsList>

      {COLLECTION_MALLS.map((mall) => (
        <TabsContent key={mall} value={mall}>
          <MallEventConsentTerms eventId={eventId} mall={mall} />
        </TabsContent>
      ))}
    </Tabs>
  );
};
